'use client';

import * as React from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/Modules/Core/Components/shadcn/input';
import { TAG_BADGE_CLASS_NAME } from '@/Modules/Core/Components/Tags/tagUtils';

export interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  value?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxHeight?: string;
  className?: string;
  allowCustomValues?: boolean;
}

const removeActionClassName = cn(
  'inline-flex items-center justify-center border bg-transparent text-zinc-400 shadow-none transition-colors',
  'hover:border-zinc-600 hover:bg-zinc-900/70 hover:text-zinc-100',
  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35',
  'dark:border-zinc-700 dark:bg-transparent dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70'
);

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value = [],
      onChange,
      placeholder = 'Select options...',
      searchPlaceholder = 'Search options...',
      maxHeight = '300px',
      className,
      allowCustomValues = false,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [selectedValues, setSelectedValues] = React.useState<string[]>(value);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(
      ref,
      () => containerRef.current as HTMLDivElement
    );

    React.useEffect(() => {
      if (value) {
        setSelectedValues(value);
      }
    }, [value]);

    React.useEffect(() => {
      if (isOpen) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }, [isOpen]);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }

      return undefined;
    }, [isOpen]);

    const normalizedSearchTerm = searchTerm.trim();
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchTerm.toLowerCase())
    );

    const selectedOptions = selectedValues.map((selectedValue) => {
      const matchedOption = options.find(
        (option) => option.value === selectedValue
      );

      return {
        value: selectedValue,
        label: matchedOption?.label ?? selectedValue,
      };
    });

    const canCreateCustomValue =
      allowCustomValues &&
      normalizedSearchTerm !== '' &&
      !options.some(
        (option) =>
          option.value.toLowerCase() === normalizedSearchTerm.toLowerCase() ||
          option.label.toLowerCase() === normalizedSearchTerm.toLowerCase()
      ) &&
      !selectedValues.some(
        (selectedValue) =>
          selectedValue.toLowerCase() === normalizedSearchTerm.toLowerCase()
      );

    const handleSelectOption = (optionValue: string) => {
      let newValues: string[];

      if (selectedValues.includes(optionValue)) {
        newValues = selectedValues.filter((item) => item !== optionValue);
      } else {
        newValues = [...selectedValues, optionValue];
      }

      setSelectedValues(newValues);
      onChange?.(newValues);
    };

    const handleRemoveTag = (
      event: React.MouseEvent<HTMLButtonElement>,
      optionValue: string
    ) => {
      event.stopPropagation();
      const newValues = selectedValues.filter((item) => item !== optionValue);
      setSelectedValues(newValues);
      onChange?.(newValues);
    };

    const handleClearAll = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setSelectedValues([]);
      onChange?.([]);
    };

    const handleCreateCustomValue = React.useCallback(() => {
      if (!canCreateCustomValue) {
        return;
      }

      const customValue = normalizedSearchTerm;
      const newValues = [...selectedValues, customValue];
      setSelectedValues(newValues);
      onChange?.(newValues);
      setSearchTerm('');
    }, [canCreateCustomValue, normalizedSearchTerm, onChange, selectedValues]);

    const handleTriggerKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen((previousValue) => !previousValue);
      }

      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const triggerClassName = cn(
      'flex min-h-10 w-full items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-[13px] text-[#111827] shadow-none dark:border-input dark:bg-background dark:text-foreground',
      'transition-[border-color,box-shadow,background-color] outline-none',
      'focus-visible:border-[#6366F1] focus-visible:ring-[3px] focus-visible:ring-[#6366F1]/18 dark:focus-visible:border-ring dark:focus-visible:ring-ring/35',
      isOpen && 'border-[#6366F1]/60 bg-white dark:border-ring/80 dark:bg-background'
    );

    return (
      <div ref={containerRef} className={cn('relative w-full', className)}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          onClick={() => setIsOpen((previousValue) => !previousValue)}
          onKeyDown={handleTriggerKeyDown}
          className={triggerClassName}
        >
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            {selectedValues.length === 0 ? (
              <span className="text-[#9CA3AF] dark:text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map(({ label, value: optionValue }) => {
                return (
                  <span
                    key={optionValue}
                    className={TAG_BADGE_CLASS_NAME}
                  >
                    <span className="truncate">{label}</span>
                    <button
                      type="button"
                      onClick={(event) => handleRemoveTag(event, optionValue)}
                      className={cn(
                        removeActionClassName,
                        'h-6 w-6 shrink-0 rounded-full border-zinc-700/80 p-0'
                      )}
                      aria-label={`Remove ${label}`}
                    >
                      <X className="h-3 w-3 stroke-[2.6]" />
                    </button>
                  </span>
                );
              })
            )}
          </div>

          <div className="ml-2 flex items-center gap-1 text-[#6B7280] dark:text-muted-foreground">
            {selectedValues.length > 0 ? (
              <button
                type="button"
                onClick={handleClearAll}
                className={cn(
                  removeActionClassName,
                  'h-7 w-7 shrink-0 rounded-md border-zinc-700/80'
                )}
                aria-label="Clear all selections"
              >
                <X className="h-3.5 w-3.5 stroke-[2.5]" />
              </button>
            ) : null}
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </div>

        {isOpen ? (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-md">
            <div className="border-b border-border/70 bg-popover p-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && canCreateCustomValue) {
                      event.preventDefault();
                      handleCreateCustomValue();
                    }
                  }}
                  className="h-10 rounded-lg border-input bg-background pl-8 pr-3 shadow-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
                />
              </div>
            </div>

            <div style={{ maxHeight }} className="overflow-y-auto bg-popover p-1.5">
              {canCreateCustomValue ? (
                <button
                  type="button"
                  onClick={handleCreateCustomValue}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-transparent px-3 py-2.5 text-left text-[13px] text-foreground shadow-none transition-colors hover:border-border/60 hover:bg-black/[0.02] dark:hover:bg-white/[0.04]"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-dashed border-primary/60 bg-primary/5 text-primary dark:bg-primary/10">
                    +
                  </span>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    Add "{normalizedSearchTerm}"
                  </span>
                </button>
              ) : null}

              {filteredOptions.length === 0 && !canCreateCustomValue ? (
                <div className="px-3 py-3 text-[13px] text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isChecked = selectedValues.includes(option.value);

                  return (
                    <label
                      key={option.value}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-lg border border-transparent px-3 py-2.5 text-[13px] text-zinc-800 transition-colors hover:border-border/60 hover:bg-black/[0.02] hover:text-zinc-950 dark:text-zinc-100 dark:hover:bg-white/[0.04] dark:hover:text-zinc-50',
                        isChecked && 'text-zinc-950 dark:text-zinc-50'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleSelectOption(option.value)}
                        className="sr-only"
                        aria-label={option.label}
                      />

                      <span
                        className={cn(
                          'flex h-5 w-5 items-center justify-center rounded border',
                          isChecked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-zinc-300 bg-white text-transparent dark:border-zinc-700 dark:bg-zinc-950'
                        )}
                      >
                        <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                      </span>

                      <span className="min-w-0 flex-1 truncate select-none">
                        {option.label}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
